import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Switch, useLocation, Route, useHistory,
} from 'react-router-dom';
import pick from 'lodash/pick';
import { PENDING, PRISTINE, SUCCESS } from 'utils/constants/queryStatuses';
import { CHAT_DISEASES } from 'utils/constants/chat';
import { AFTER_SIGNUP_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import { profileToQuestionnaireParameters } from 'functions/profileToQuestionnaireParameters';
import { PageHeader } from 'components/PageHeader/PageHeader';
import { PageHeaderProvider } from 'components/PageHeader/PageHeaderContext';
import { CombinedProviders } from 'components/CombinedProviders/CombinedProviders';
import app from 'new-ui/app';
import PrivateRoute from 'new-ui/Components/PrivateRoute';
import PublicRoute from 'new-ui/Components/PublicRoute';
import { POPUP } from 'utils/constants/popup';
import DashboardFooter from 'new-ui/Footer';
import { MatchesSearch } from 'pages/MatchesSearch';
import { Matches } from 'pages/Matches';
import { Trial } from 'pages/Trial';
import { Treatment } from 'pages/Treatment';
import { PublicTrial } from 'pages/PublicTrial';
import { PublicTreatment } from 'pages/PublicTreatment';
import { Intake } from 'pages/Intake';
import { Login } from 'new-ui/Login/Login';
import { ForgotPassword } from 'new-ui/Login/ForgotPassword';
import { ResetPassword } from 'new-ui/Login/ResetPassword';
import Maintenance from 'new-ui/Components/Maintenance';
import { Upload } from 'pages/Upload';
import { FinanceSupport } from 'pages/FinanceSupport';
import { Patients } from 'pages/Patients';
import { Feed } from 'pages/Feed/Feed';
import { Articles } from 'pages/Articles';
import { ChatButton } from 'components/ChatButton/ChatButton';
import { FinanceIntake } from 'pages/FinanceIntake';
import ChatPage from 'pages/Chat';
import { MatchesFilterProvider } from 'modules/matchesFilter/MatchesFilterContext';
import { useAuth } from 'utils/hooks/useAuth';
import { useMatchesFetching } from 'modules/matches/useMatchesFetching';
import { getUserCondition } from 'modules/user/utils';
import { intercom } from 'modules/intercom/intercom';
import { useBackdrop } from 'modules/backdrop/useBackdrop';
import { ROUTE_PATH, ROUTE_NAME } from 'utils/constants/router';
import { getFirstMatchedRoute } from 'functions/getFirstMatchedRouteName';

const BACKDROP_REASON = 'dashboard_init';

const pageProviders = [
  { provider: PageHeaderProvider },
  { provider: MatchesFilterProvider },
];

const getMatchedRouteName = (pathname) => getFirstMatchedRoute(
  [
    ROUTE_NAME.TREATMENT, ROUTE_NAME.PUBLIC_TREATMENT, ROUTE_NAME.RESULTS, ROUTE_NAME.FEED,
  ].map((name) => ({ name, path: ROUTE_PATH[name] })),
  pathname,
)?.name;

const Dashboard = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const {
    profile: { data: profile },
    matches: { status: matchesStatus },
    appConfiguration: { providerSpecific: { code: providerCode } = {} },
  } = useSelector((state) => pick(state, ['profile', 'appConfiguration', 'matches']));
  const [, setAppInitializationCount] = useState(0);

  const { initializationStatus: appInitializationStatus } = app;

  app.history = useHistory();

  if (appInitializationStatus === PRISTINE) {
    app.init(setAppInitializationCount);
  }

  const params = new URLSearchParams(window.location.search);

  if (params.get(AFTER_SIGNUP_URL_SEARCH_PARAM)) {
    app.isSignup = true;
  }

  useMatchesFetching();

  useBackdrop({ isShown: appInitializationStatus === PENDING, reason: BACKDROP_REASON });

  const isMatchesFetched = matchesStatus === SUCCESS;

  useEffect(() => {
    // matches fetching is usually happens after user update
    // should account user update
    if (!isMatchesFetched) {
      return;
    }
    if (!intercom.user) {
      intercom.boot();
    } else {
      intercom.update();
    }
  }, [isMatchesFetched]);

  let gettingQuestionnaireParameters = isAuthenticated ? profileToQuestionnaireParameters(profile) : null;
  gettingQuestionnaireParameters = gettingQuestionnaireParameters && JSON.stringify(gettingQuestionnaireParameters);

  useEffect(() => {
    if (!gettingQuestionnaireParameters) {
      return;
    }
    app.initQuestions(...JSON.parse(gettingQuestionnaireParameters));
  }, [gettingQuestionnaireParameters]);

  const matchedRouteName = getMatchedRouteName(location.pathname);

  const isTreatmentPage = [ROUTE_NAME.PUBLIC_TREATMENT, ROUTE_NAME.TREATMENT].includes(matchedRouteName);
  const isResultsPage = ROUTE_NAME.RESULTS === matchedRouteName;

  const isChatButtonShown = appInitializationStatus === SUCCESS
    && isAuthenticated
    && CHAT_DISEASES.includes(getUserCondition(profile))
    && [ROUTE_NAME.RESULTS, ROUTE_NAME.FEED].includes(matchedRouteName);

  return appInitializationStatus === SUCCESS ? (
    <div className={`app new-ui app-${window.location.pathname.slice(1)}`}>
      <div>
        <CombinedProviders providers={pageProviders}>
          <PageHeader isProviderMode={!!providerCode} />
          <main className={`main-${isTreatmentPage ? 'treatment' : window.location.pathname.slice(1)}`}>
            <Switch>
              <PrivateRoute path={ROUTE_PATH[ROUTE_NAME.RESULTS]} component={Matches} exact />
              <PrivateRoute path={ROUTE_PATH[ROUTE_NAME.SUMMARY]} redirect={ROUTE_PATH[ROUTE_NAME.RESULTS]} exact />
              <PrivateRoute path={ROUTE_PATH[ROUTE_NAME.TRIAL]} component={Trial} exact />
              <PrivateRoute path={ROUTE_PATH[ROUTE_NAME.TREATMENT]} component={Treatment} exact />
              <PrivateRoute path={ROUTE_PATH[ROUTE_NAME.INTAKE]} component={Intake} exact />
              <Route path={ROUTE_PATH[ROUTE_NAME.FINANCE]} component={FinanceSupport} />
              <PrivateRoute path={ROUTE_PATH[ROUTE_NAME.FEED]} component={Feed} />
              <PrivateRoute path={ROUTE_PATH[ROUTE_NAME.ARTICLES]} component={Articles} />
              <PrivateRoute path={ROUTE_PATH[ROUTE_NAME.CHAT]} component={ChatPage} />
              <Route path={ROUTE_PATH[ROUTE_NAME.FINANCE_INTAKE]} component={FinanceIntake} />
              <PrivateRoute path={ROUTE_PATH[ROUTE_NAME.PATIENTS]} component={Patients} />
              <PublicRoute path={ROUTE_PATH[ROUTE_NAME.FORGOT_PASSWORD]} component={ForgotPassword} exact />
              <PublicRoute path={ROUTE_PATH[ROUTE_NAME.FORGOT_PASSWORD]} component={ForgotPassword} exact />
              <PublicRoute path={ROUTE_PATH[ROUTE_NAME.NEW_PASSWORD]} component={ResetPassword} exact />
              <PublicRoute path={ROUTE_PATH[ROUTE_NAME.LOGIN]} component={Login} />
              <Route path={ROUTE_PATH[ROUTE_NAME.SEARCH]} component={MatchesSearch} exact />
              <Route path={ROUTE_PATH[ROUTE_NAME.UPLOAD]} component={Upload} exact />
              <Route path={ROUTE_PATH[ROUTE_NAME.MAINTENANCE]} component={Maintenance} exact />
              <Route path={ROUTE_PATH[ROUTE_NAME.PUBLIC_TREATMENT]} component={PublicTreatment} />
              <Route
                path={ROUTE_PATH[ROUTE_NAME.PUBLIC_FINANCE]}
                render={(props) => <FinanceSupport isPublicFinance {...props} />}
                exact
              />
              {[ROUTE_PATH[ROUTE_NAME.PUBLIC_TRIAL], ROUTE_PATH[ROUTE_NAME.PUBLIC_TRIAL_WITH_CONDITION]].map(
                (path) => <Route key={path} path={path} component={PublicTrial} exact />,
              )}
            </Switch>
          </main>
        </CombinedProviders>
        <DashboardFooter />
        {isChatButtonShown && (
          <ChatButton
            popupType={isResultsPage ? POPUP.NBA_CHAT : POPUP.ASSISTANCE_CHAT}
            text="Ask AI Oncology Expert"
          />
        )}
      </div>
    </div>
  ) : null;
};

export default Dashboard;
